import {ComponentRef, inject, Injectable, Injector, RendererFactory2, Type} from '@angular/core';
import {DIALOG_COMPONENT_OPTIONS, DialogBaseComponent} from '../../components/dialog/base/dialog.base.component';
import {ContainerService} from '~shared/ng/services/container/container.service';
import {ContainerComponent} from '~shared/ng/components/dialog/container/container.component';
import {BehaviorSubject, Observable, Subject, take} from 'rxjs';
import {BackdropComponent} from '~shared/ng/components/dialog/backdrop/backdrop.component';
import {distinctUntilChanged, map, shareReplay, skip} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DOCUMENT} from '@angular/common';
import {
    DialogSimpleComponent,
    DialogSimpleComponentOptions
} from '~shared/ng/components/dialog/simple/dialog.simple.component';

@Injectable()
export class DialogService {
    protected static readonly CLASS_SCROLLBAR_MEASURE = 'modal-scrollbar-measure';
    protected static readonly CLASS_MODAL_OPEN = 'modal-open';
    public static readonly CLASS_SHOW = 'show';

    private readonly containerService = inject(ContainerService);
    private readonly injector = inject(Injector);
    private readonly document: Document = inject(DOCUMENT);
    private readonly renderer = inject(RendererFactory2).createRenderer(null, null);

    private _containerComponent: ContainerComponent;
    protected readonly _modals$ = new BehaviorSubject<ReadonlyArray<ComponentRef<DialogBaseComponent<any>>>>([]);
    protected readonly us$ = new Subject<void>();

    constructor() {
        let isBodyOverflowing = false;
        let scrollbarWidth = 0;
        let bodyPaddingRight = '';

        this.isModalListEmpty$
            .pipe(
                skip(1),
                takeUntilDestroyed()
            )
            .subscribe(($) => {
                if ($) {
                    // Body
                    if (isBodyOverflowing) {
                        // Original padding should be restored if required.
                        if (bodyPaddingRight && bodyPaddingRight !== '') {
                            this.renderer.setStyle(this.document.body, 'padding-right', bodyPaddingRight);
                        } else {
                            this.renderer.removeStyle(this.document.body, 'padding-right');
                        }
                    }
                    this.renderer.removeClass(this.document.body, DialogService.CLASS_MODAL_OPEN);
                    this.renderer.removeStyle(this.document.body, 'overflow');

                } else {
                    // Body
                    const rect = this.document.body.getBoundingClientRect();
                    isBodyOverflowing = rect.left + rect.right < window.innerWidth;
                    if (isBodyOverflowing) {
                        scrollbarWidth = this.getScrollbarWidth();

                        // Adjust body padding (if <body> tags style has paddingRight property save it to restore later).
                        bodyPaddingRight = this.document.body.style.paddingRight;

                        const calculatedPadding = parseFloat(window.getComputedStyle(this.document.body).getPropertyValue('padding-right'));
                        this.renderer.setStyle(this.document.body, 'padding-right', (calculatedPadding + scrollbarWidth) + 'px');
                    }
                    this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
                }
            });
    }

    public create<T extends DialogBaseComponent<Partial<O>>, O = any>($component: Type<T>, $options?: O): T {
        let component = this.container.appendComponent(
            $component,
            Injector.create({
                providers: [
                    {
                        provide: DIALOG_COMPONENT_OPTIONS,
                        useValue: $options
                    }
                ],
                parent: this.injector
            }),
        );

        component.instance.hidden$
            .pipe(take(1))
            .subscribe(() => {
                // Destroy component.
                const index = this.modals.indexOf(component)
                this.container.remove(index + 1)
                this.modals = this.modals.slice(0, index).concat(this.modals.slice(index + 1));
            });

        this.modals = this.modals.concat(component);
        return component.instance;
    }

    public createSimple($options: DialogSimpleComponentOptions): DialogSimpleComponent {
        return this.create(DialogSimpleComponent, $options);
    }

    protected getScrollbarWidth(): number {
        const scrollDiv = this.document.createElement('div');
        this.document.body.appendChild(scrollDiv);
        const scrollbarWidth = window.innerWidth - scrollDiv.getBoundingClientRect().width;
        this.document.body.removeChild(scrollDiv);
        return scrollbarWidth;
    }

    protected get container(): ContainerComponent {
        if (!this._containerComponent) {
            this._containerComponent = this.containerService.provideEmptyContainer();
            this._containerComponent.appendComponent(BackdropComponent)
        }
        return this._containerComponent;
    }

    public get isModalListEmpty$(): Observable<boolean> {
        return this._modals$.pipe(
            map($ => $.length === 0),
            distinctUntilChanged(),
            shareReplay(1)
        );
    }

    protected set modals($value: ReadonlyArray<ComponentRef<DialogBaseComponent<any>>>) {
        this._modals$.next($value);
    }

    protected get modals(): ReadonlyArray<ComponentRef<DialogBaseComponent<any>>> {
        return this._modals$.value;
    }

}
